import { useState, useCallback } from 'react';

const STORAGE_KEY = 'exochronometer_notes';

const loadNotes = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Validate and normalize a single imported note entry
const normalizeNote = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  if (typeof raw.text !== 'string' || raw.text.length === 0) return null;
  const timestamp = Number(raw.timestamp);
  if (!Number.isFinite(timestamp)) return null;
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : crypto.randomUUID(),
    timestamp,
    text: raw.text,
    degrees: raw.degrees && typeof raw.degrees === 'object' ? { ...raw.degrees } : {},
  };
};

export function useNotes() {
  const [notes, setNotes] = useState(loadNotes);

  const addNote = useCallback((text, currentDegrees) => {
    const note = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      text,
      degrees: { ...currentDegrees },
    };
    setNotes((prev) => {
      const updated = [...prev, note];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const deleteNote = useCallback((id) => {
    setNotes((prev) => {
      const updated = prev.filter((n) => n.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Import notes from a JSON string (exported file or data copied from
  // the old exochronometer.vercel.app site). Accepts a bare array of
  // notes or an object with a `notes` array. Merges with existing notes,
  // skipping duplicates (same id, or same timestamp + text).
  // Returns { imported, skipped } counts; throws on unparseable input.
  const importNotes = useCallback((jsonString) => {
    let parsed = JSON.parse(jsonString.trim());
    // Tolerate double-encoded JSON (a quoted string containing JSON)
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    const rawList = Array.isArray(parsed) ? parsed : parsed && Array.isArray(parsed.notes) ? parsed.notes : null;
    if (!rawList) {
      throw new Error('Expected a JSON array of journal entries');
    }
    let imported = 0;
    let skipped = 0;
    setNotes((prev) => {
      const existingIds = new Set(prev.map((n) => n.id));
      const existingKeys = new Set(prev.map((n) => `${n.timestamp}|${n.text}`));
      const additions = [];
      for (const raw of rawList) {
        const note = normalizeNote(raw);
        if (!note) {
          skipped += 1;
          continue;
        }
        if (existingIds.has(note.id) || existingKeys.has(`${note.timestamp}|${note.text}`)) {
          skipped += 1;
          continue;
        }
        existingIds.add(note.id);
        existingKeys.add(`${note.timestamp}|${note.text}`);
        additions.push(note);
        imported += 1;
      }
      if (additions.length === 0) return prev;
      const updated = [...prev, ...additions].sort((a, b) => a.timestamp - b.timestamp);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
    return { imported, skipped, total: rawList.length };
  }, []);

  // Download all notes as a JSON file
  const exportNotes = useCallback(() => {
    const current = loadNotes();
    const blob = new Blob([JSON.stringify(current, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `exochronometer-journal-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  return { notes, addNote, deleteNote, importNotes, exportNotes };
}
